import { APP_CONFIG } from '../config/AppConfig'
import { SESSION_SERVICE } from '../services/SessionService'
import { HttpVerb } from '../constants/HttpVerb'
import { Readable } from 'stream'; // Or 'node:stream' if Node-only
import { MimeTypes } from '../constants/MimeTypes';

function isReadable(obj: any): obj is Readable {
  return obj && typeof obj.pipe === 'function' && typeof obj._read === 'function';
}

function readStreamToBuffer(stream: Readable): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    stream.on('data', chunk => chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk)));
    stream.on('end', () => resolve(Buffer.concat(chunks)));
    stream.on('error', reject);
  });
}

const API_VERSION = 'v1'

const getStage = () => {
    if (APP_CONFIG.isLocal() || APP_CONFIG.isAlpha()) {
        return 'alpha'
    } else if (APP_CONFIG.isProduction()) {
        return 'prod'
    }

    throw new Error('An unknown stage has been detected.')
}

export const fetchData = async (path: string, verb: HttpVerb = HttpVerb.GET, body?: Readable | ArrayBuffer | string, contentType?: string) => {
    try {
        const endpointUrl = [
            APP_CONFIG.getRestApiUrl(),
            getStage(),
            API_VERSION,
            path
        ].join('/')

        let preparedBody: BodyInit | undefined;

        if (isReadable(body)) {
            // convert Readable to Buffer due to type error
            preparedBody = await readStreamToBuffer(body);
        } else {
            preparedBody = body;
        }

        // Assume JSON for string types
        contentType ??= typeof body === 'string'
            ? MimeTypes.JSON
            : MimeTypes.BINARY

        // Get API key from config
        const apiKey = APP_CONFIG.getApiKey()
        
        // Get current user email for the custom header
        const session = SESSION_SERVICE.getCurrentSession()
        const userEmail = session.user.email || ''

        const response: Response = await fetch(endpointUrl, {
            headers: {
                'x-api-key': apiKey,
                'x-user-email': userEmail,
                'Content-Type': contentType!
            },
            credentials: 'include',
            method: verb as string,
            body: preparedBody,
            mode: 'cors',
        })

        const result = await response.text() // to force the fetch to complete before we check the status

        if (response.status === 204) {
            return Promise.resolve(undefined)
        }

        if (!response.ok) {
            throw new Error(`Status: ${response.status}, statusText: ${response.statusText}, message: ${result}`)
        }

        return Promise.resolve(result.length > 0 ? JSON.parse(result) : '') //.blob()
    } catch (e: any) {
        console.error('MiddleTier=>fetchData', e)
        console.error('MiddleTier=>fetchData=>message', e.message)
        console.error('MiddleTier=>fetchData=>stack', e.stack)
        throw e
    }
}

export const fetchDataSync = (path: string, verb: HttpVerb = HttpVerb.GET, body?: string, contentType?: string) => {
    console.log("Fetching sync data from path:", path, "with verb:", verb)

        const endpointUrl = [
            APP_CONFIG.getRestApiUrl(),
            getStage(),
            API_VERSION,
            path
        ].join('/')

        // Assume JSON for string types
        contentType ??= typeof body === 'string'
            ? MimeTypes.JSON
            : MimeTypes.BINARY

        // Get API key from config
        const apiKey = APP_CONFIG.getApiKey()
        
        // Get current user email for the custom header
        const session = SESSION_SERVICE.getCurrentSession()
        const userEmail = session.user.email || ''

        console.log("Going to call fetch()")
        fetch(endpointUrl, {
            headers: {
                'x-api-key': apiKey,
                'x-user-email': userEmail,
                'Content-Type': contentType!
            },
            credentials: 'include',
            method: verb as string,
            body: body,
            mode: 'cors',
        }).then(r => console.log("Fetch sync response:", r)).catch(e => console.error("Fetch sync error:", e))

        console.log("Fetch sync past")
}

export const fetchDataBlob = async (path: string, verb: HttpVerb = HttpVerb.GET, body?: Readable | ArrayBuffer | string, contentType?: string) => {
    try {
        const endpointUrl = [
            APP_CONFIG.getRestApiUrl(),
            getStage(),
            API_VERSION,
            path
        ].join('/')

        let preparedBody: BodyInit | undefined;

        if (isReadable(body)) {
            // convert Readable to Buffer due to type error
            preparedBody = await readStreamToBuffer(body);
        } else {
            preparedBody = body;
        }

        // Assume JSON for string types
        contentType ??= typeof body === 'string'
            ? MimeTypes.JSON
            : MimeTypes.BINARY

        // Get API key from config
        const apiKey = APP_CONFIG.getApiKey()
        
        // Get current user email for the custom header
        const session = SESSION_SERVICE.getCurrentSession()
        const userEmail = session.user.email || ''

        const response: Response = await fetch(endpointUrl, {
            headers: {
                'x-api-key': apiKey,
                'x-user-email': userEmail,
                'Content-Type': contentType!
            },
            credentials: 'include',
            method: verb as string,
            body: preparedBody,
            mode: 'cors',
        })

        const result = await response.blob() // to force the fetch to complete before we check the status

        if (!response.ok) {
            throw new Error(`Status: ${response.status}, message: ${result}`)
        }

        return Promise.resolve(result)
    } catch (e: any) {
        console.error('MiddleTier=>fetchDataBlob', e)
        throw e
    }
}
